import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';
import PhotoOutlinedIcon from '@mui/icons-material/PhotoOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  createImageExport,
  downloadImageExport,
  listImageExports,
  type ImageExportJob,
  type ImageExportScope,
  type ImageExportStatus,
} from '../lib/api';

/**
 * Admin tools → Download images. Kicks off a SERVER-SIDE bulk image export (hero
 * images only, or the full category tree) and shows its progress. Because the zip
 * is built on the server, the operator can close or restart their browser while
 * it runs and come back to download it — the job list below is the source of
 * truth, polled while anything is in progress. Gated to Admin + User in the
 * router and nav.
 */
export function ImageExport() {
  const [jobs, setJobs] = useState<ImageExportJob[]>([]);
  const [scope, setScope] = useState<ImageExportScope>('all');
  const [shortlistedOnly, setShortlistedOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      setJobs(await listImageExports());
    } catch {
      // A transient poll failure shouldn't blow away a good jobs list — the next
      // tick will recover.
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Poll while a build is queued/running; stop once everything is settled.
  const hasActive = jobs.some((j) => j.status === 'queued' || j.status === 'running');
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => void refresh(), 2500);
    return () => clearInterval(t);
  }, [hasActive, refresh]);

  const handleCreate = async () => {
    setCreating(true);
    setError(null);
    try {
      await createImageExport(scope, shortlistedOnly);
      await refresh();
    } catch {
      setError('Could not start the export. Please try again, or contact support if it keeps happening.');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (id: number) => {
    setDownloadingId(id);
    setError(null);
    try {
      await downloadImageExport(id);
    } catch {
      setError('That download is no longer available — it may have expired. Please run a fresh export.');
      await refresh();
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Stack spacing={3} alignItems="center">
      <Typography variant="h3" component="h1" align="center">
        Download images
      </Typography>

      <Paper sx={{ p: { xs: 3, sm: 4 }, maxWidth: 820, width: '100%' }}>
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            This page allows you to export either Hero images only, or all images, for every project.
            Choose from 'Hero only' or 'All images' below, and you will see a new export start in the
            'Recent exports' section. Once completed, a 'Download' button will appear on the right.
            Large exports can take some time, feel free to leave this page and come back later as the
            compiling is completed server-side.
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <ExportOption
              icon={<PhotoOutlinedIcon fontSize="large" sx={{ color: 'text.primary' }} />}
              title="Hero images only"
              description="A single folder containing all hero images. Naming convention: <project_id>_Hero.jpg"
              selected={scope === 'hero'}
              disabled={creating}
              onSelect={() => setScope('hero')}
            />
            <ExportOption
              icon={<CollectionsOutlinedIcon fontSize="large" sx={{ color: 'text.primary' }} />}
              title="All images"
              description="All images for every project organised into categories/sub-categories."
              selected={scope === 'all'}
              disabled={creating}
              onSelect={() => setScope('all')}
            />
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <FormControlLabel
              control={
                <Switch
                  checked={shortlistedOnly}
                  onChange={(e) => setShortlistedOnly(e.target.checked)}
                  disabled={creating}
                />
              }
              label="Shortlisted projects only"
            />
            <Button
              variant="contained"
              size="large"
              onClick={handleCreate}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {creating ? 'Starting…' : 'Prepare export'}
            </Button>
          </Stack>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 820, width: '100%' }}>
        <Typography variant="h6" gutterBottom>
          Recent exports
        </Typography>
        {jobs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No exports yet. Choose an option above to create one.
          </Typography>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />} spacing={0}>
            {jobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                downloading={downloadingId === job.id}
                onDownload={() => handleDownload(job.id)}
              />
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  );
}

// A selectable scope card. Clicking selects the scope; the actual export is
// started by the shared "Prepare export" button below the two cards.
function ExportOption({
  icon,
  title,
  description,
  selected,
  disabled,
  onSelect,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      role="button"
      aria-pressed={selected}
      onClick={disabled ? undefined : onSelect}
      sx={(t) => ({
        p: 2.5,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        alignItems: 'flex-start',
        cursor: disabled ? 'default' : 'pointer',
        borderWidth: 2,
        borderColor: selected ? 'primary.main' : 'transparent',
        outline: selected ? 'none' : `1px solid ${t.palette.divider}`,
        outlineOffset: '-1px',
        backgroundColor: selected
          ? t.palette.mode === 'dark'
            ? 'rgba(255,204,0,0.10)'
            : 'rgba(255,204,0,0.10)'
          : undefined,
        transition: 'border-color 120ms, background-color 120ms',
      })}
    >
      <Box
        sx={(t) => ({
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: t.palette.mode === 'dark' ? 'rgba(255,204,0,0.16)' : 'rgba(255,204,0,0.22)',
        })}
      >
        {icon}
      </Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
        {description}
      </Typography>
    </Paper>
  );
}

const STATUS_META: Record<ImageExportStatus, { label: string; color: 'default' | 'info' | 'success' | 'error' | 'warning' }> = {
  queued: { label: 'Queued', color: 'info' },
  running: { label: 'Preparing', color: 'info' },
  ready: { label: 'Ready', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  expired: { label: 'Expired', color: 'warning' },
};

function JobRow({
  job,
  downloading,
  onDownload,
}: {
  job: ImageExportJob;
  downloading: boolean;
  onDownload: () => void;
}) {
  const meta = STATUS_META[job.status] ?? { label: job.status, color: 'default' as const };
  const scopeLabel =
    (job.scope === 'hero' ? 'Hero images' : 'All images') + (job.shortlisted_only ? ' · Shortlisted' : '');
  const isActive = job.status === 'queued' || job.status === 'running';
  const pct =
    job.images_total > 0 ? Math.round(((job.images_done + job.images_failed) / job.images_total) * 100) : 0;

  return (
    <Box sx={{ py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} flexWrap="wrap">
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={meta.label} color={meta.color} />
            <Typography variant="subtitle2">{scopeLabel}</Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {formatWhen(job)} {job.actor ? `· ${job.actor}` : ''}
          </Typography>
        </Stack>

        {job.status === 'ready' && (
          <Button
            variant="contained"
            size="small"
            onClick={onDownload}
            disabled={downloading}
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <FileDownloadOutlinedIcon />}
          >
            {downloading ? 'Downloading…' : `Download (${formatBytes(job.file_bytes)})`}
          </Button>
        )}
      </Stack>

      {isActive && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress
            variant={job.images_total > 0 ? 'determinate' : 'indeterminate'}
            value={pct}
          />
          <Typography variant="caption" color="text.secondary">
            {job.images_total > 0
              ? `${job.images_done + job.images_failed} of ${job.images_total} images (${pct}%)`
              : 'Gathering images…'}
          </Typography>
        </Box>
      )}

      {job.status === 'ready' && job.images_failed > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {job.images_done} images included · {job.images_failed} skipped (unreadable).
        </Typography>
      )}

      {job.status === 'failed' && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {job.error_message || 'The export failed. Please try again.'}
        </Alert>
      )}

      {job.status === 'expired' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          This export has expired and been removed. Run a fresh one to download again.
        </Typography>
      )}
    </Box>
  );
}

function formatWhen(job: ImageExportJob): string {
  const iso = job.completed_at || job.started_at || job.created_at;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function formatBytes(bytes: number): string {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
